import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ExcelService } from '@services/excel.service';
import { ExcelObject } from '@services/util.service';
import { decompress } from 'lz-string';
import { BehaviorSubject, map, Subject, combineLatest, takeUntil, Observable } from 'rxjs';
import { Bizoim, BizoimClientResponse, BizoimClient, BizoimConcentrated, BizoimConcentratedClients } from 'src/app/Interfaces/agent-sales-quantities';
import { ClientGoals, ClientGoalsWithGroups } from 'src/app/Interfaces/goals';
import { TDashDocSub } from 'src/app/Interfaces/reports';
import { ResponseFromExpress } from 'src/app/Interfaces/SQL-interfaces';
import { LoadingService } from 'src/app/services/loading.service';
import { StoreService } from 'src/app/services/store.service';
import { translateDateForSQLServer } from 'src/app/Utility/date_functions/SQL_ISO_adapter';
import { utils, writeFile } from 'xlsx';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
type ClientGroupData = {
	total: number;
	lastYearTotal: number;
	advancementPercentage: number;
	growthPercentage: number;
	currentYearGoal: number;
};

type ClientGoalsMainDisplay = ClientGroupData & {
	groupName: string;
	groupId: string;
	isOpenClients: boolean;
	isOpenGroupOnly: boolean;
};
type ClientsWithGroupsExpansionDisplay = ClientGoalsMainDisplay & {
	clientId: string;
	clientName: string;
};

@Component({
	selector: 'app-client-group-sales-report',
	templateUrl: './client-group-sales-report.component.html',
	styleUrls: ['./client-group-sales-report.component.scss'],
})
export class ClientGroupSalesReportComponent implements OnInit, OnDestroy, AfterViewInit {
	private _ss = inject(StoreService);
	private router = inject(Router);
	private _loading = inject(LoadingService);
	private _excel = inject(ExcelService);

	constructor() {}

	mustPickDatePopup = true;
	hasPickedAtLeastOnce = false;

	AllProducts;
	AllProductsSearch;

	allClientGoals$ = new BehaviorSubject<ClientGoalsWithGroups[]>([]);
	clientGroupChosen$ = new BehaviorSubject<string | null>(null);

	groupAllClientsChosen$ = new BehaviorSubject<string | null>(null);

	clientsWithoutAnyTotalOrLastYearTotal = new Set<string>();

	year = currentYear;

	@ViewChild('clientTableWrapper') clientTableWrapper: ElementRef<HTMLDivElement>;
	@ViewChild('groupTableWrapper') groupTableWrapper: ElementRef<HTMLDivElement>;
	@ViewChild('onlyGroupTableWrapper') onlyGroupTableWrapper: ElementRef<HTMLDivElement>;
	@ViewChild('reportTableWrapper') reportTableWrapper: ElementRef<HTMLDivElement>;

	allClientsGoalsGrouped$: Observable<ClientsWithGroupsExpansionDisplay[]> = combineLatest([this.allClientGoals$, this.clientGroupChosen$, this.groupAllClientsChosen$]).pipe(
		map(([goals, groupChosen, onlyGroupChosen]) =>
			Object.values<ClientsWithGroupsExpansionDisplay>(
				goals
					.filter(goal => goal.Yy1 === currentYear && Number(goal.Mm1) <= currentMonth)
					.reduce((acc, goal) => {
						acc[goal.Cl] ||= {
							total: 0,
							lastYearTotal: 0,
							advancementPercentage: 0,
							growthPercentage: 0,
							currentYearGoal: 0,
							groupName: goal.gn,
							groupId: goal.cg,
							// isOpen: groupChosen === goal.cg,
							isOpenClients: groupChosen === goal.cg,
							isOpenGroupOnly: onlyGroupChosen === goal.cg,
							clientId: goal.Cl,
							clientName: goal.Cn,
						};
						const group = acc[goal.Cl];
						group.total += goal.Tot;
						group.lastYearTotal += goal.Ltot;
						group.currentYearGoal += goal.Yaad2 || goal.Yaad1 || 0;
						const { total, lastYearTotal, currentYearGoal } = group;
						if (total && currentYearGoal) {
							group.advancementPercentage = (total / currentYearGoal) * 100;
						}

						if (total && lastYearTotal) {
							group.growthPercentage = (total / lastYearTotal) * 100;
						}
						return acc;
					}, {} as Record<string, ClientsWithGroupsExpansionDisplay>),
			),
		),
	);
	allClientGoalsDisplay$: Observable<ClientGoalsMainDisplay[]> = combineLatest([this.allClientsGoalsGrouped$, this.clientGroupChosen$]).pipe(
		map(([goals]) =>
			Object.values<ClientGoalsMainDisplay>(
				goals.reduce((acc, goal) => {
					acc[goal.groupId] ||= {
						total: 0,
						lastYearTotal: 0,
						advancementPercentage: 0,
						growthPercentage: 0,
						currentYearGoal: 0,
						groupName: goal.groupName,
						groupId: goal.groupId,
						isOpenClients: goal.isOpenClients,
						isOpenGroupOnly: goal.isOpenGroupOnly,
					};
					const group = acc[goal.groupId];
					group.total += goal.total;
					group.lastYearTotal += goal.lastYearTotal;
					group.currentYearGoal += goal.currentYearGoal;
					const { total, lastYearTotal, currentYearGoal } = group;
					if (total && currentYearGoal) {
						group.advancementPercentage = (total / currentYearGoal) * 100;
					}

					if (total && lastYearTotal) {
						group.growthPercentage = (total / lastYearTotal) * 100;
					}
					return acc;
				}, {}),
			),
		),
	);

	clientSelected$ = new BehaviorSubject<string | null>(null);

	clientsUnderGroup$: Observable<ClientsWithGroupsExpansionDisplay[]> = combineLatest([this.allClientsGoalsGrouped$, this.clientGroupChosen$, this.clientSelected$]).pipe(
		map(([goals, groupId, clientId]) => goals.filter(goal => goal.groupId === groupId).map(client => ({ ...client, isOpenGroupOnly: client.clientId === clientId }))),
	);

	monthlyGoalsOfSelectedClientOrGroup$ = combineLatest([this.allClientGoals$, this.clientSelected$, this.groupAllClientsChosen$]).pipe(
		map(([goals, clientId, groupID]) => {
			goals = goals.filter(goal => goal.Yy1 === currentYear && !this.clientsWithoutAnyTotalOrLastYearTotal.has(goal.Cl));
			const filteredGoals = groupID
				? Object.values<ClientGoalsWithGroups>(
						goals
							.filter(goal => goal.cg === groupID)
							.reduce((prev, curr) => {
								prev[curr.Mm1] ||= {
									...curr,
									Tot: 0,
									Ltot: 0,
									Yaad1: 0,
									Yaad2: 0,
								};

								const month = prev[curr.Mm1];

								month.Tot += curr.Tot;
								month.Ltot += curr.Ltot;
								month.Yaad1 += curr.Yaad1 || 0;
								month.Yaad2 += curr.Yaad2 || 0;

								return prev;
							}, {} as Record<string, ClientGoalsWithGroups>),
				  ).sort((a, b) => Number(a.Mm1) - Number(b.Mm1))
				: goals.filter(goal => goal.Cl === clientId);
			// .filter(goal => goal.Cl === clientId && goal.Yy1 === currentYear)
			return filteredGoals.map(goal => {
				const value = {
					total: goal.Tot,
					lastYearTotal: goal.Ltot,
					advancementPercentage: 0,
					growthPercentage: 0,
					currentYearGoal: goal.Yaad2 || goal.Yaad1 || 0,
					month: goal.Mm1,
				};
				if (value.total && value.currentYearGoal) {
					value.advancementPercentage = (value.total / value.currentYearGoal) * 100;
				}
				if (value.total && value.lastYearTotal) {
					value.growthPercentage = (value.total / value.lastYearTotal) * 100;
				}
				return value;
			});
		}),
	);

	BizoimCompany;

	toogleInputNew = false;
	ShowCompanys = null;
	HasMultipleCompanys;
	reportSearch = null;
	history = history;

	destroy$ = new Subject<void>();

	loadingMainQuery = false;

	windowWidth: number;

	isAdmin = location.pathname.includes('admin');

	AgentArray = [];

	loadingAgents = false;

	hasGetAgentSalesNextQuery = false;
	hasGetAgentOrdersNextQuery = false;
	hasGetAgentSalesNextQueryConcentrated = false;

	First = true;
	FirstScrollY = 0;
	Second = false;
	Third = false;

	isConcentrated = location.pathname.includes('concentrated');

	isOrders = location.pathname.includes('orders');

	agentsToGet = [];

	productIDChosen = '';
	clientIDChosen = '';

	ngOnInit(): void {
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////
		if (this._ss.CustomerInformation === null) {
			if (localStorage.getItem('CustomerInformation') !== null) {
				this._ss.CustomerInformation = decompress(localStorage.getItem('CustomerInformation'));
				this._ss.CustomerInformation = JSON.parse(this._ss.CustomerInformation);
			}
		}
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////

		this.HasMultipleCompanys = localStorage.getItem('HasMultipleCompanys');

		this.windowWidth = window.innerWidth;

		if (this.HasMultipleCompanys === '2') {
			if (localStorage.getItem('ShowCompanys') !== null) {
				this.ShowCompanys = localStorage.getItem('ShowCompanys');
				this.ShowCompanys = JSON.parse(this.ShowCompanys);
			} else {
				this._ss.GetComppanys().subscribe(res => {
					this.ShowCompanys = res;
					this.ShowCompanys = this.ShowCompanys.recordset;
					localStorage.setItem('ShowCompanys', JSON.stringify(this.ShowCompanys));
				});
			}
		}

		window.scrollTo(0, 0);

		if (!localStorage.getItem('UserName') && !localStorage.getItem('AgentId')) {
			this.router.navigate(['login']);
			return;
		}

		this._ss.UserNameConnected = localStorage.getItem('UserName');
		this._ss.AgentIdConnected = localStorage.getItem('AgentId');

		this.getInitialData();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit(): void {
		this.calculateTableHeight();
	}

	handleClosePopup() {
		this.mustPickDatePopup = false;
		if (!this.hasPickedAtLeastOnce) {
			history.back();
			return;
		}
	}

	getInitialData() {
		this._loading.changeLoadingStatuses({ isLoading: true });

		this._ss.getGoalsClientsWithGroups().subscribe({
			next: res => {
				if (res === 'ConnectionError' || res === 'RequestError') {
					this.handleError();
					return;
				}

				this.allClientGoals$.next(res.recordset);

				setTimeout(() => {
					this.calculateTableHeight();
				}, 0);

				// const clientsWithoutAnyTotalOrLastYearTotal = res.recordset
				// 	.filter(goal => goal.Yy1 === currentYear && Number(goal.Mm1) <= currentMonth)
				// 	.reduce((acc, goal) => {
				// 		acc[goal.Cl] ||= { Tot: 0, Ltot: 0 };
				// 		acc[goal.Cl].Tot += goal.Tot;
				// 		acc[goal.Cl].Ltot += goal.Ltot;
				// 		return acc;
				// 	}, []);

				// this.clientsWithoutAnyTotalOrLastYearTotal = new Set(
				// 	Object.entries(clientsWithoutAnyTotalOrLastYearTotal)
				// 		.filter(([_, { Tot, Ltot }]) => Tot === 0 && Ltot === 0)
				// 		.map(([clientId]) => clientId),
				// );

				this._loading.changeLoadingStatuses({ isLoading: false });
			},
			error: err => {
				this.handleError();
			},
		});
	}

	handleError() {
		this._loading.changeLoadingStatuses({ isLoading: false });
		alert('ישנה תקלת תקשורת, אנא נסה שנית מאוחר יותר');
		history.back();
		return;
	}

	handleNavigation() {
		history.back();
	}

	async getAgents(): Promise<void> {
		if (this._ss.CustomerInformation.Submanagers === '1') {
			this.agentsToGet = await this.getSubmanagerAgentsFirst();
		}

		this.getAgentUsers();
	}

	async getSubmanagerAgentsFirst(): Promise<string[]> {
		return await new Promise(resolve => {
			if (!this._ss.agentsOfSubmanager.length) {
				this._ss.getAdminAgents().subscribe({
					next: res => {
						if (typeof res === 'string') {
							alert('חלה תקלה בהבאת סוכנים עבור מנהל זה. נסו שנית');
							resolve([]);
							history.back();
							return;
						}

						const agents = res.recordset.map(obj => obj.Agent);
						agents.push(this._ss.AgentIdConnected);
						this._ss.agentsOfSubmanager = agents;

						return resolve(agents);
					},
					error: err => {
						alert('שגיאת שרת');
					},
				});
			} else {
				resolve(this._ss.agentsOfSubmanager);
			}
		});
	}

	getAgentUsers() {
		this._ss.GetAllAgents(this.agentsToGet).subscribe({
			next: res => {
				if (res === 'ConnectionError' || res === 'RequestError') {
					alert('אירעה שגיאה בקבלת נתונים מהשרת, אנא נסה שנית');
					history.back();
					return;
				}

				this.AgentArray = res.recordset;
				this.loadingAgents = false;
			},
			error: err => {
				alert('אירעה שגיאה בקבלת נתונים מהשרת, אנא נסה שנית');
				history.back();
			},
		});
	}

	toggleGroup(groupId: string) {
		// const table = this.groupTable?.nativeElement;
		// const currentGroup = this.clientGroupChosen$.value;
		// const isClosing = currentGroup === groupId;

		// if (isClosing) {
		// 	if (table) {
		// 		table.classList.remove('active');
		// 	}
		// 	setTimeout(() => {
		// 		this.clientGroupChosen$.next(null);
		// 	}, 200);
		// } else {
		// 	this.clientGroupChosen$.next(groupId);

		// 	setTimeout(() => {
		// 		const table = this.groupTable?.nativeElement;
		// 		if (table) {
		// 			table.classList.add('active');
		// 		}
		// 	}, 0);
		// }
		this.groupAllClientsChosen$.next(null);
		this.clientGroupChosen$.next(this.clientGroupChosen$.value === groupId ? null : groupId);
		if (this.clientGroupChosen$.value === groupId) {
			this.clientSelected$.next(null);
		}
		setTimeout(() => {
			const table = this.groupTableWrapper?.nativeElement;
			if (table) {
				table.classList.add('active');
			}
		}, 0);
	}

	toggleOnlyGroup(groupId: string) {
		this.clientSelected$.next(null);
		this.clientGroupChosen$.next(null);
		this.groupAllClientsChosen$.next(this.groupAllClientsChosen$.value === groupId ? null : groupId);
		setTimeout(() => {
			const table = this.onlyGroupTableWrapper?.nativeElement;
			if (table) {
				table.classList.add('active');
			}
		}, 0);
	}

	toggleClient(clientId: string) {
		// this.groupAllClientsChosen$.next(null);
		this.clientSelected$.next(this.clientSelected$.value === clientId ? null : clientId);

		setTimeout(() => {
			const table = this.clientTableWrapper?.nativeElement;
			if (table) {
				table.classList.add('active');
			}
		}, 0);
	}

	onExcelDownload() {
		const rawData = this.allClientGoals$.value.map(goal => ({
			'קוד לקוח': goal.Cl,
			'שם לקוח': goal.Cn,
			שנה: goal.Yy1,
			חודש: goal.Mm1,
			'סה"כ': goal.Tot,
			אשתקד: goal.Ltot,
			'השוואה לאשתקד': goal.Ahuz,
			חברה: goal.cm,
			יעד: goal.Yaad1,
			'יעד 2': goal.Yaad2,
			'קוד קבוצה': goal.cg,
			'שם קבוצה': goal.gn,
		}));

		this._excel.downloadCustomExcel(rawData, {
			filename: 'מכירות לקוחות לפי קבוצה',
			autoFilter: true,
			rtl: true,
			sheetName: 'מכירות',
		});
	}

	calculateTableHeight() {
		const table = this.reportTableWrapper?.nativeElement;

		if (!table) {
			return;
		}

		const { top } = table.getBoundingClientRect();
		const padding = 35;

		table.style.height = `calc(100vh - ${top + padding}px)`;
	}
}
